import {
    faAdd,
    faArrowLeft,
    faSave,
    faStickyNote,
    faTrash,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useQuery } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import Button from '../../../components/Button';
import ButtonLink from '../../../components/ButtonLink';
import ConfirmationModal from '../../../components/ConfirmationModal';
import Loader from '../../../components/Loader';
import LoaderDot from '../../../components/LoaderDot';
import SideNav from '../../../components/SideNav';
import { noteIdPage, notePage } from '../../../constants/pagesRoutesContants';
import { classNames } from '../../../helpers/classNames';
import NavDataInterface from '../../../interfaces/NavDataInterface';
import { fetchNotes } from '../../../services/noteService';
import NoteType from '../../../types/NoteType';
import NoteDetails, { NoteDetailsHandle } from './noteDetails';
import styles from './style.module.scss';

function Notes() {
    const { noteId = '' } = useParams();
    const noteDetailsRef = useRef<NoteDetailsHandle>(null);
    const [modalOpened, setModalOpened] = useState<boolean>(false);
    const [newNote, setNewNote] = useState<boolean>(false);

    const { isPending: isPendingNotes, data: notes } = useQuery({
        queryKey: ['notes'],
        queryFn: async () => {
            try {
                const { data } = await fetchNotes();
                return data || [];
            } catch (error) {
                console.error(error);
            }
            return [];
        },
    });

    const navData: NavDataInterface[] = notes?.map(
        (note: NoteType): NavDataInterface => {
            return {
                id: note.id || '',
                label: `${note.title}`,
                subLabel:
                    `${note.clientName || ''} ${(note.clientName && note.clientCaseName ? ' - ' : '') + (note.clientCaseName ? note.clientCaseName : '')}`.trim(),
                date: note.lastEditDate ? note.lastEditDate : note.creationDate,
                link: noteIdPage.path + note.id,
                variant: 'secondaryLight',
                icon: faStickyNote,
                iconColor: '#ede02e',
            };
        }
    );

    const selectedNote = notes?.find((note: NoteType) => note.id === noteId);

    const handleClickNewNote = () => {
        setNewNote(true);
    };
    const handleCloseNote = () => {
        setNewNote(false);
    };
    const handleSaveSelectedNote = () => {
        noteDetailsRef.current?.saveSelectedNote();
    };
    const handleDeleteSelectedNote = () => {
        setModalOpened(true);
    };

    const handleClickConfirmDeleteNote = () => {
        noteDetailsRef.current?.deleteSelectedNote();
    };

    if (isPendingNotes) {
        <Loader />;
    }

    return (
        <div className={styles.container}>
            <div
                className={classNames([
                    styles.sideNav,
                    !!noteId || newNote ? styles.noteOpened : '',
                ])}
            >
                <div className={styles.sideNavHeader}>
                    <div className={styles.headerTitle}>Notes</div>
                    <ButtonLink
                        variant="secondaryLight"
                        className={styles.newNote}
                        link={notePage.path}
                        onClick={handleClickNewNote}
                    >
                        <FontAwesomeIcon icon={faAdd} />{' '}
                        <span className={styles.newNoteText}>
                            Nouvelle Note
                        </span>
                    </ButtonLink>
                </div>
                <div className={styles.sideNavContent}>
                    {isPendingNotes ? (
                        <LoaderDot />
                    ) : (
                        <SideNav
                            selected=""
                            navData={navData}
                            hasSearchBar={true}
                            searchBarPlaceholder="Titre, client, dossier..."
                        />
                    )}
                </div>
            </div>
            {(!!noteId || newNote) && (
                <div className={styles.body}>
                    <ConfirmationModal
                        modalOpened={modalOpened}
                        setModalOpened={setModalOpened}
                        confirmationAction={handleClickConfirmDeleteNote}
                    >
                        Voulez-vous supprimer cette note?
                    </ConfirmationModal>
                    <div className={styles.bodyHeader}>
                        <ButtonLink
                            variant="secondaryLight"
                            link={notePage.path}
                            onClick={handleCloseNote}
                        >
                            <FontAwesomeIcon icon={faArrowLeft} />
                        </ButtonLink>
                        <div className={styles.bodyTitleContainer}>
                            <div className={styles.bodyTitle}>
                                {selectedNote?.title || 'Nouvelle note'}
                            </div>
                            {!!noteId && (
                                <div className={styles.bodySubTitle}>
                                    Dernière modification{': '}
                                    {selectedNote?.lastEditDate
                                        ? new Date(
                                              selectedNote.lastEditDate
                                                  ? selectedNote.lastEditDate
                                                  : selectedNote.creationDate
                                          ).toLocaleString()
                                        : ''}
                                </div>
                            )}
                        </div>
                        <div className={styles.bodyActionContainer}>
                            <Button
                                variant="secondary"
                                className={styles.saveButton}
                                onClick={handleSaveSelectedNote}
                            >
                                <FontAwesomeIcon icon={faSave} /> Enregistrer
                            </Button>
                            <Button
                                variant="delete"
                                className={styles.saveButton}
                                onClick={handleDeleteSelectedNote}
                            >
                                <FontAwesomeIcon icon={faTrash} />
                            </Button>
                        </div>
                    </div>
                    <div
                        className={classNames([
                            styles.bodyContent,
                            !!noteId || newNote ? styles.noteOpened : '',
                        ])}
                    >
                        {(noteId.length > 0 || newNote) && (
                            <NoteDetails ref={noteDetailsRef} noteId={noteId} />
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

export default Notes;
